import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Save, AlertTriangle, History, PackagePlus, X } from "lucide-react";
import { api, fmt, fmtPerUnit, pluralUnit, apiError } from "@/lib/api";

const MOVE_TYPES = ["order_placed", "order_cancelled", "purchase_received", "return_restock", "admin_adjust", "reserved", "released"];
const MOVE_LABEL = { order_placed: "Order", order_cancelled: "Cancel restock", purchase_received: "Purchase", return_restock: "Return restock", admin_adjust: "Adjustment", reserved: "Reserved", released: "Released" };

function MovementsTab() {
  const [moves, setMoves] = useState(null);
  const [type, setType] = useState("");
  const [sku, setSku] = useState("");

  const load = useCallback(() => {
    api.get("/admin/inventory/movements", { params: { ...(type && { movement_type: type }), ...(sku && { sku }) } })
      .then(({ data }) => setMoves(data.movements))
      .catch((e) => toast.error(apiError(e, "Couldn't load movements")));
  }, [type, sku]);
  useEffect(load, [load]);

  return (
    <div data-testid="inventory-movements-tab">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3">
          <Search className="h-3.5 w-3.5 text-neutral-500" />
          <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="Filter by SKU…" data-testid="movements-sku-filter"
            className="h-9 w-36 bg-transparent text-xs text-white placeholder:text-neutral-600 focus:outline-none" />
        </div>
        {["", ...MOVE_TYPES].map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`movements-filter-${t || "all"}`}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${type === t ? "border-amber-500/50 bg-amber-500/15 text-amber-300" : "border-white/10 text-neutral-500 hover:text-white"}`}>
            {t ? MOVE_LABEL[t] : "All"}
          </button>
        ))}
      </div>
      {!moves ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : moves.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] py-16 text-center text-sm text-neutral-500" data-testid="movements-empty">
          No inventory movements yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
          <table className="w-full min-w-[760px] text-left text-sm" data-testid="movements-table">
            <thead>
              <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                <th className="px-5 py-3.5">When</th><th className="px-5 py-3.5">SKU</th><th className="px-5 py-3.5">Change</th>
                <th className="px-5 py-3.5">Type</th><th className="px-5 py-3.5">Stock after</th><th className="px-5 py-3.5">Notes</th>
              </tr>
            </thead>
            <tbody>
              {moves.map((m) => (
                <tr key={m.id} className="border-b border-white/[0.04]" data-testid={`movement-row-${m.id}`}>
                  <td className="whitespace-nowrap px-5 py-3 text-xs text-neutral-500">{new Date(m.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-5 py-3 font-mono text-xs font-bold text-amber-400">{m.sku}</td>
                  <td className={`px-5 py-3 text-sm font-bold ${m.qty_change > 0 ? "text-emerald-400" : m.qty_change < 0 ? "text-red-400" : "text-neutral-500"}`}>
                    {m.qty_change > 0 ? `+${m.qty_change}` : m.qty_change || "—"}
                  </td>
                  <td className="px-5 py-3"><span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-300">{MOVE_LABEL[m.movement_type] || m.movement_type}</span></td>
                  <td className="px-5 py-3 text-xs text-neutral-400">{m.balance_after ?? "—"}</td>
                  <td className="max-w-64 truncate px-5 py-3 text-xs text-neutral-500" title={m.notes}>{m.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdjustDialog({ row, onClose, onDone }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const d = parseInt(delta);
    if (Number.isNaN(d) || d === 0) { toast.error("Enter a non-zero adjustment (e.g. 10 or -3)"); return; }
    if (reason.trim().length < 3) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/inventory/adjust", { product_id: row.product_id, sku: row.sku, delta: d, reason: reason.trim() });
      toast.success(`${row.sku} adjusted by ${d > 0 ? "+" : ""}${d}`);
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Couldn't adjust stock"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="adjust-dialog"
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#FFFEFA] p-6">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold text-white">Adjust stock</h3>
          <button onClick={onClose} aria-label="Close" className="text-neutral-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-1 text-xs text-neutral-500">{row.product_name} · <span className="font-mono text-amber-400">{row.sku}</span> · current {row.stock}</p>
        <label className="mt-4 block">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Adjustment (+/-)</span>
          <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 25 or -3" data-testid="adjust-delta"
            className="h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3.5 text-sm text-white focus:border-amber-500/60 focus:outline-none" />
        </label>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Reason (required)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Cycle count, damage, correction…" data-testid="adjust-reason"
            className="h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3.5 text-sm text-white focus:border-amber-500/60 focus:outline-none" />
        </label>
        <button onClick={submit} disabled={busy} data-testid="adjust-submit"
          className="mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-amber-500 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply adjustment
        </button>
      </div>
    </div>
  );
}

export default function AdminInventory() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [edits, setEdits] = useState({});
  const [savingSku, setSavingSku] = useState("");
  const [tab, setTab] = useState("stock");
  const [adjustRow, setAdjustRow] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/inventory", { params: { q, low: lowOnly } })
      .then(({ data }) => { setRows(data); setEdits({}); })
      .catch(() => toast.error("Couldn't load inventory"));
  }, [q, lowOnly]);
  useEffect(load, [load]);

  const save = async (row) => {
    const edit = edits[row.sku] || {};
    const stock = parseInt(edit.stock ?? row.stock);
    const threshold = parseInt(edit.low_stock_threshold ?? row.low_stock_threshold);
    if (Number.isNaN(stock) || stock < 0) {
      toast.error("Stock must be zero or more");
      return;
    }
    setSavingSku(row.sku);
    try {
      await api.put(`/admin/products/${row.product_id}/variants/${row.sku}/stock`, { stock, low_stock_threshold: threshold });
      toast.success(`${row.sku} stock set to ${stock}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update stock"));
    } finally {
      setSavingSku("");
    }
  };

  return (
    <div data-testid="admin-inventory">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4">
          <Search className="h-4 w-4 shrink-0 text-neutral-500" />
          <input data-testid="admin-inventory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product or SKU…" className="h-11 w-full bg-transparent text-sm text-white placeholder:text-neutral-600 focus:outline-none" />
        </div>
        <button
          data-testid="admin-inventory-low-toggle"
          onClick={() => setLowOnly((v) => !v)}
          className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-xs font-bold transition-all ${
            lowOnly ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400 hover:border-white/25"
          }`}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Low Stock Only
        </button>
        <div className="flex rounded-xl border border-white/10 p-1" data-testid="inventory-tabs">
          {[["stock", "Stock"], ["movements", "Movements"]].map(([v, label]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`inventory-tab-${v}`}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold transition-colors ${tab === v ? "bg-amber-500 text-black" : "text-neutral-400 hover:text-white"}`}>
              {v === "movements" && <History className="h-3.5 w-3.5" />}{label}
            </button>
          ))}
        </div>
      </div>

      {tab === "movements" && <MovementsTab />}

      {tab === "stock" && (!rows ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] py-16 text-center text-sm text-neutral-500" data-testid="admin-inventory-empty">
          No inventory rows match.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
          <table className="w-full min-w-[860px] text-left text-sm" data-testid="admin-inventory-table">
            <thead>
              <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                <th className="px-5 py-4">Product</th>
                <th className="px-5 py-4">SKU</th>
                <th className="px-5 py-4">Variant</th>
                <th className="px-5 py-4">Price</th>
                <th className="px-5 py-4">Stock</th>
                <th className="px-5 py-4">Reserved</th>
                <th className="px-5 py-4">Available</th>
                <th className="px-5 py-4">MOQ / Step</th>
                <th className="px-5 py-4">Low Threshold</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Save</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const edit = edits[row.sku] || {};
                const dirty = edit.stock !== undefined || edit.low_stock_threshold !== undefined;
                return (
                  <tr key={row.sku} className="border-b border-white/[0.04]" data-testid={`inventory-row-${row.sku}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <img src={row.image} alt="" className="h-9 w-9 rounded-lg object-cover" loading="lazy" />
                        <span className="max-w-44 truncate font-semibold text-white">{row.product_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs font-bold text-amber-400">{row.sku}</td>
                    <td className="px-5 py-3.5 text-xs text-neutral-400">{row.label}</td>
                    <td className="px-5 py-3.5 text-neutral-300">{fmtPerUnit(row.price, row.unit)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min="0"
                          data-testid={`inventory-stock-${row.sku}`}
                          value={edit.stock ?? row.stock}
                          onChange={(e) => setEdits((s) => ({ ...s, [row.sku]: { ...s[row.sku], stock: e.target.value } }))}
                          className="h-9 w-24 rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none"
                        />
                        <span className="text-[11px] text-neutral-600">{pluralUnit(row.unit, 2)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-center text-sm font-bold text-sky-300" data-testid={`inventory-reserved-${row.sku}`}>
                      {row.reserved || 0}
                    </td>
                    <td className="px-5 py-3.5 text-center text-sm font-bold text-white" data-testid={`inventory-available-${row.sku}`}>
                      {(row.stock || 0) - (row.reserved || 0)}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-neutral-400" data-testid={`inventory-moq-${row.sku}`}>
                      {row.min_order_qty || 1} / {row.qty_increment || 1}
                    </td>
                    <td className="px-5 py-3.5">
                      <input
                        type="number"
                        min="0"
                        data-testid={`inventory-threshold-${row.sku}`}
                        value={edit.low_stock_threshold ?? row.low_stock_threshold}
                        onChange={(e) => setEdits((s) => ({ ...s, [row.sku]: { ...s[row.sku], low_stock_threshold: e.target.value } }))}
                        className="h-9 w-16 rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-xs text-neutral-300 focus:border-amber-500/60 focus:outline-none"
                      />
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                        row.status === "out"
                          ? "border-red-500/30 bg-red-500/10 text-red-400"
                          : row.status === "low"
                            ? "border-amber-500/30 bg-amber-500/10 text-amber-400"
                            : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                      }`} data-testid={`inventory-status-${row.sku}`}>
                        {row.status === "out" ? "Out of Stock" : row.status === "low" ? "Low Stock" : "In Stock"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`inventory-adjust-${row.sku}`}
                          onClick={() => setAdjustRow(row)}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 text-[11px] font-bold text-amber-300 transition-colors hover:bg-amber-500/20"
                        >
                          <PackagePlus className="h-3.5 w-3.5" /> Adjust
                        </button>
                        <button
                          data-testid={`inventory-save-${row.sku}`}
                          onClick={() => save(row)}
                          disabled={!dirty || savingSku === row.sku}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-emerald-500/50 hover:text-emerald-400 disabled:opacity-30"
                          aria-label="Save stock"
                        >
                          {savingSku === row.sku ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {adjustRow && (
        <AdjustDialog row={adjustRow} onClose={() => setAdjustRow(null)}
          onDone={() => { setAdjustRow(null); load(); }} />
      )}
    </div>
  );
}
